/* eslint-disable func-names */

interface Button {
    action: (this: StepContext) => unknown;
    classes: string;
}

interface StepContext {
    next: () => unknown;
    cancel: () => unknown;
    complete: () => void;
    manager: {
        getWalkthrough: (name: string) => {
            updateStateOpen: (state: string) => void;
        };
    };
}

interface AttachToConfig {
    attachTo: {
        element: string;
        on?: string;
    };
    advanceOn?: {
        selector: string;
        event: string;
        preventDefault: boolean;
    };
}

interface Step {
    id: string;
    buttons?: Button[];
    applicableToPath?: string;
    skipWhenElementExists?: string;
    waitForElement?: string;
    forceMenuOpenById?: string;
    translationKeys?: Record<string, string>;
    attachTo?: {
        element: string;
        on?: string;
    };
    advanceOn?: {
        selector: string;
        event: string;
        preventDefault: boolean;
    };
}

interface Walkthrough {
    name: string;
    steps: Step[];
}

interface SchemaParams {
    demoMeasuringPoint: string | number;
    demoProject: string | number;
}

const primaryButton = 'pure-button pure-button-primary button-table';
const secondaryButton = primaryButton.replace('primary', 'secondary');

function button(action: (this: StepContext) => unknown, secondary?: boolean): Button {
    return {
        action,
        classes: secondary ? secondaryButton : primaryButton,
    };
}

function buttonNext(secondary?: boolean): Button {
    return button(function (this: StepContext) {
        return this.next();
    }, secondary);
}

function buttonCancel(): Button {
    return button(function (this: StepContext) {
        return this.cancel();
    }, true);
}

interface AttachToOptions {
    on?: string;
    advanceOnEvent?: string;
    advanceOnElement?: string;
    preventDefault?: boolean;
}

function attachTo(element: string, options: AttachToOptions = {}): AttachToConfig {
    const config: AttachToConfig = {
        attachTo: {
            element,
        },
    };

    if (options.on) {
        config.attachTo.on = options.on;
    }

    if (options.advanceOnEvent) {
        config.advanceOn = {
            selector: options.advanceOnElement ?? element,
            event: options.advanceOnEvent,
            preventDefault: options.preventDefault ?? false,
        };
    }

    return config;
}

const OVERVIEW_PATH = '/overview/';

// The demo measuring points are in projects, which only the By project tab lists.
const BY_PROJECT_TAB_STEP = {
    id: 'by-project-tab',
    applicableToPath: OVERVIEW_PATH,
    skipWhenElementExists: '#overview-tab-by_project[aria-selected="true"]',
    ...attachTo('#overview-tab-by_project', {
        advanceOnEvent: 'click',
    }),
};

function applicableToSbrView(): { applicableToPath: string } {
    return {
        applicableToPath: '/sbr/sbrView/',
    };
}

function switchWalkthroughButton(name: string): Button {
    return button(function (this: StepContext) {
        this.complete();
        this.manager.getWalkthrough(name).updateStateOpen('');
    });
}

function schema({ demoMeasuringPoint, demoProject }: SchemaParams): Walkthrough[] {
    const demoMeasuringPointEl = `tr[data-measuring-point-id="${demoMeasuringPoint}"]`;
    const expandDemoProjectEl = `#expand-project-${demoProject}`;
    // The demo measuring point is in a project, which starts collapsed. The step is skipped when
    // the project is expanded already.
    const expandDemoProjectStep = {
        id: 'expand-project',
        applicableToPath: OVERVIEW_PATH,
        skipWhenElementExists: `${expandDemoProjectEl}[aria-expanded="true"]`,
        ...attachTo(expandDemoProjectEl, {
            advanceOnEvent: 'click',
        }),
    };
    // Only the first page of projects is listed. A search brings up the demo project and expands
    // it, so this step is skipped when the demo project is on the page already. The projects have
    // loaded when any of them is on the page.
    const searchDemoProjectStep = {
        id: 'search-demo-project',
        applicableToPath: OVERVIEW_PATH,
        waitForElement: '[id^="expand-project-"]',
        skipWhenElementExists: expandDemoProjectEl,
        ...attachTo('#measuring-point-overview-search', {
            advanceOnEvent: 'input',
        }),
    };
    const undoSharingItemId = `undo-sharing-measuring_point-${demoMeasuringPoint}`;

    return [
        {
            name: 'getting-started',
            steps: [
                {
                    id: 'ask-to-start',
                    buttons: [buttonCancel(), buttonNext()],
                },
                {
                    id: 'owns-swarm-question',
                    buttons: [
                        switchWalkthroughButton('measuring-point-page'),
                        switchWalkthroughButton('graph'),
                    ],
                },
            ],
        },

        {
            name: 'measuring-point-page',
            steps: [
                {
                    id: 'click-measuring-point',
                    ...attachTo('#id_measuring_point', {
                        advanceOnEvent: 'mouseenter',
                    }),
                },
                {
                    id: 'click-create-measuring-point',
                    forceMenuOpenById: 'id_measuring_point',
                    ...attachTo('#id_create_measuring_point', {
                        advanceOnEvent: 'mouseenter',
                    }),
                },
                {
                    id: 'click-create-vibration-measuring-point',
                    forceMenuOpenById: 'id_create_measuring_point',
                    ...attachTo('#id_measuring_point_create_vibration', {
                        advanceOnEvent: 'click',
                    }),
                },
                {
                    id: 'enter-mp-name',
                    applicableToPath: '/measuring_point/create/vibration',
                    ...attachTo('#id_name', {
                        advanceOnElement: 'form:not(.en):not(.nl):not(.de):not(.fr)',
                        advanceOnEvent: 'submit',
                        preventDefault: true,
                    }),
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                    buttons: [buttonNext()],
                },
                {
                    id: 'enter-swarm-name',
                    applicableToPath: '/measuring_point/create/vibration',
                    ...attachTo('#id_deviceName', {
                        on: 'left',
                        advanceOnElement: 'form:not(.en):not(.nl):not(.de):not(.fr)',
                        advanceOnEvent: 'submit',
                        preventDefault: true,
                    }),
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                {
                    id: 'swarm-settings',
                    ...attachTo('#swarmSettings', {
                        on: 'auto',
                    }),
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                {
                    id: 'alarm-settings',
                    ...attachTo('#alarmLevels', {
                        on: 'auto',
                    }),
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                {
                    id: 'alarm-recipient-settings',
                    ...attachTo('#alarmRecipientSettings', {
                        on: 'auto',
                    }),
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                {
                    id: 'measurement-schedule',
                    ...attachTo('#measurementSchedule', {
                        on: 'auto',
                    }),
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                {
                    id: 'click-save',
                    ...attachTo('#save-form', {
                        on: 'auto',
                        advanceOnElement: '#save-form',
                        advanceOnEvent: 'click',
                    }),
                },
                {
                    id: 'congratulations',
                    buttons: [switchWalkthroughButton('graph')],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
            ],
        },
        {
            name: 'graph',
            steps: [
                {
                    id: 'start',
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                BY_PROJECT_TAB_STEP,
                searchDemoProjectStep,
                expandDemoProjectStep,
                {
                    id: 'view-data',
                    applicableToPath: OVERVIEW_PATH,
                    ...attachTo(`${demoMeasuringPointEl} a[href^="/sbr/sbrView/"]`, {
                        advanceOnEvent: 'click',
                    }),
                },
                {
                    id: 'graph-types',
                    ...applicableToSbrView(),
                    ...attachTo('#data-type-selector', { on: 'bottom' }),
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                {
                    id: 'graph',
                    ...applicableToSbrView(),
                    ...attachTo('#graph-velocity .graph-area', {
                        on: 'bottom',
                    }),
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                {
                    id: 'pan-zoom',
                    ...applicableToSbrView(),
                    ...attachTo('#graph-velocity .plot', { on: 'auto' }),
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                {
                    id: 'brush-graph',
                    ...applicableToSbrView(),
                    ...attachTo('#graph-velocity .brushGraph .overlay', {
                        on: 'auto',
                    }),
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                {
                    id: 'view-filter-buttons',
                    ...applicableToSbrView(),
                    ...attachTo('#graph-velocity .graph-buttons .view-types', {
                        on: 'auto',
                    }),
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                {
                    id: 'display-buttons',
                    ...applicableToSbrView(),
                    // Keep this selector synced with GraphToolbar
                    // wrapper class `graph-display-types`.
                    ...attachTo('#graph-velocity .graph-buttons .graph-display-types', {
                        on: 'auto',
                    }),
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                {
                    id: 'zoom-buttons',
                    ...applicableToSbrView(),
                    // Keep this selector synced with GraphToolbar
                    // wrapper class `graph-zoom-types`.
                    ...attachTo('#graph-velocity .graph-buttons .graph-zoom-types', { on: 'auto' }),
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                {
                    id: 'export',
                    ...applicableToSbrView(),
                    ...attachTo('#graph-velocity #exports', { on: 'auto' }),
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                {
                    id: 'switch-mp',
                    ...applicableToSbrView(),
                    ...attachTo(
                        'span[aria-labelledby="select2-measuring-point-selector-container"]',
                        { on: 'auto' }
                    ),
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                {
                    id: 'show-help-menu',
                    ...applicableToSbrView(),
                    ...attachTo('#id_help', { on: 'auto' }),
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
                {
                    id: 'end',
                    ...applicableToSbrView(),
                    ...attachTo('#hubspot-messages-iframe-container'),
                    buttons: [buttonNext(), switchWalkthroughButton('remove-demo-mp')],
                    translationKeys: {
                        BUTTON_1: 'CONTINUE',
                    },
                },
            ],
        },
        {
            name: 'remove-demo-mp',
            steps: [
                {
                    ...BY_PROJECT_TAB_STEP,
                    translationKeys: {
                        TITLE: 'WALKTHROUGH_GRAPH_BY-PROJECT-TAB_TITLE',
                        TEXT: 'WALKTHROUGH_GRAPH_BY-PROJECT-TAB_TEXT',
                    },
                },
                {
                    ...searchDemoProjectStep,
                    translationKeys: {
                        TITLE: 'WALKTHROUGH_GRAPH_SEARCH-DEMO-PROJECT_TITLE',
                        TEXT: 'WALKTHROUGH_GRAPH_SEARCH-DEMO-PROJECT_TEXT',
                    },
                },
                {
                    ...expandDemoProjectStep,
                    translationKeys: {
                        TITLE: 'WALKTHROUGH_GRAPH_EXPAND-PROJECT_TITLE',
                        TEXT: 'WALKTHROUGH_GRAPH_EXPAND-PROJECT_TEXT',
                    },
                },
                {
                    id: 'hover-cog',
                    applicableToPath: OVERVIEW_PATH,
                    ...attachTo(`${demoMeasuringPointEl} button[aria-haspopup="menu"]`, {
                        advanceOnEvent: 'mouseenter',
                    }),
                },
                {
                    id: 'click-undo-sharing',
                    applicableToPath: OVERVIEW_PATH,
                    forceMenuOpenById: undoSharingItemId,
                    ...attachTo(`#${undoSharingItemId}`, {
                        advanceOnEvent: 'click',
                    }),
                },
                {
                    id: 'end',
                    buttons: [buttonNext()],
                    translationKeys: {
                        BUTTON_1: 'CLOSE',
                    },
                },
            ],
        },
    ];
}

export { button, buttonCancel, schema };
